import { ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react';
import { cn } from '../../lib/utils';

interface Column<T> {
  key: string;
  header: string;
  align?: 'left' | 'center' | 'right';
  className?: string;
  sortable?: boolean;
  render: (row: T, index: number) => React.ReactNode;
}

interface SortState {
  key: string;
  order: 'asc' | 'desc';
}

interface TableProps<T> {
  columns: Column<T>[];
  data: T[];
  keyExtractor: (row: T) => string;
  onRowClick?: (row: T) => void;
  sort?: SortState;
  onSortChange?: (sort: SortState) => void;
  className?: string;
}

export const Table = <T,>({ columns, data, keyExtractor, onRowClick, sort, onSortChange, className }: TableProps<T>) => {
  const handleSort = (key: string) => {
    if (!onSortChange) return;
    if (sort?.key === key) {
      onSortChange({ key, order: sort.order === 'asc' ? 'desc' : 'asc' });
    } else {
      onSortChange({ key, order: 'asc' });
    }
  };

  return (
    <div className={cn('border rounded-xl overflow-hidden bg-white', className)}>
      <table className="w-full">
        <thead>
          <tr className="border-b bg-gray-50">
            {columns.map(col => {
              const isSorted = sort?.key === col.key;
              const alignClass = col.align === 'right' ? 'text-right' : col.align === 'center' ? 'text-center' : 'text-left';

              return (
                <th
                  key={col.key}
                  className={cn(
                    'text-xs font-medium text-gray-500 px-4 py-3',
                    alignClass,
                    col.sortable && 'cursor-pointer select-none hover:text-gray-700',
                    col.className,
                  )}
                  onClick={col.sortable ? () => handleSort(col.key) : undefined}
                >
                  <span className={cn('inline-flex items-center gap-1', col.align === 'right' && 'flex-row-reverse')}>
                    {col.header}
                    {col.sortable && (
                      isSorted
                        ? sort.order === 'asc'
                          ? <ArrowUp size={12} className="text-gray-700" />
                          : <ArrowDown size={12} className="text-gray-700" />
                        : <ArrowUpDown size={12} className="text-gray-300" />
                    )}
                  </span>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {data.map((row, i) => (
            <tr
              key={keyExtractor(row)}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
              className={cn(
                i < data.length - 1 && 'border-b',
                'hover:bg-gray-50 transition-colors',
                onRowClick && 'cursor-pointer',
              )}
            >
              {columns.map(col => (
                <td
                  key={col.key}
                  className={cn(
                    'px-4 py-3',
                    col.align === 'right' ? 'text-right' : col.align === 'center' ? 'text-center' : 'text-left',
                    col.className,
                  )}
                >
                  {col.render(row, i)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export type { Column, TableProps, SortState };
